import { lazy } from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import type { ModuleManifest } from '../../lib/host/types';
import { reportsKeys } from './queries/keys';

// ── Story 4-128b — a THIN RE-EXPORT of the canonical document boundary ──────
// `ReportRender` (story 4-78) was this module's own component until 4-128b
// moved it to `src/report-document/ReportDocument.tsx` — the canonical pure
// document boundary P06 §4.3 mandates ("Existing report-module exports become
// thin consumers/re-exports"). Both names are exported: `ReportDocument` is
// the name §4.3's migration table gives it, `ReportRender`/`ReportRenderProps`
// are the pre-4-128b aliases every existing consumer still imports (review
// detail, report detail, the share viewer, Report Studio's canvas, the design
// gallery) — kept so a path rename never becomes a second copy, per the same
// section's "a renamed path needs an audit mapping, not a second copy".
//
// This module keeps its barrel-not-reach-in contract unchanged: the
// `no-restricted-imports` rule on every `src/modules/*/**` path still points
// callers at this file.
export { ReportDocument, ReportDocument as ReportRender } from '../../report-document';
export type { ReportDocumentProps, ReportDocumentProps as ReportRenderProps } from '../../report-document';
// The Paged.js pagination adapter (story 4-108) — the ONE integration point
// for the report editor's real, page-fragmented "Print" preview. See the
// component's own header comment for why every other report-rendering
// surface stays unaware Paged.js exists.
export { PagedReportPreview, type PagedReportPreviewProps } from './components/PagedReportPreview';
// Story 4-128d — the isolated print host's imperative surface. Report Studio's
// outline navigation needs it because the paper it scrolls now lives in
// another document; see `PagedReportPreview.tsx`'s own header.
export type { ReportPrintHostHandle } from './components/useReportPrintHost';

// Reports module (P06 §4; story 4-9a) — the staff report LIST and the staff
// report DETAIL, the two surfaces P06 §4's first bullet describes.
//
// WHY A MODULE NOW. Reports was the last first-class entity still living as a
// shell page (`src/pages/ReportsPage.tsx`, deleted this story) with its nav row
// hand-written into `src/lib/registries/index.ts`. P18 §9's rule is that an
// existing surface is retrofitted "when its wave touches it", and this is that
// touch: the list moves onto `EntityListPage` + the shared cursor contract, the
// detail screen is born, and both attach through this manifest — so `App.tsx`
// and the shell registry are not edited to ADD anything (DOD §1.8, G-21). The
// nav ROW keeps its exact key, label, permission, featureKey and order, because
// story-2-9 asserts the flag→teaser→restore behaviour of that row by name; a
// module migration must be invisible to it.
//
// `featureKeys: ['reports.designer']` — the same key the nav row already
// carried and the same key the API now enforces (`requireFeature` on every
// report route). One key, three planes: the row, the route, the endpoint.
//
// NO `entities` / `search` block, so G-21 does not apply. A report is reached
// FROM its inspection or from this list; nothing indexes reports for Cmd+K
// today, and declaring an entity here without an indexer is the half-registered
// shape G-21 exists to reject. When reports become searchable that is a story
// that adds `entities` and `search` together, which the manifest union enforces.
//
// SHARE LINKS (story 6-1a, P06 §6.1) now live here too — the launch slice of
// the Wave-6 policy guard the comment above used to defer entirely. Password/
// OTP protection, comments, e-signature and the client portal are still NOT
// here (6-1b/6-2/6-3 own them); this module ships only the expiring/revocable
// view-link surface: the create/manage control on the report detail screen
// and the SEPARATE unauthenticated `/share/:token` route (a top-level `src/
// pages/` route, not a module route — see `App.tsx`'s own comment on why an
// unauthenticated surface cannot go through the module host, which gates on
// an authenticated capability document).
export const manifest: ModuleManifest = {
  key: 'reports',
  featureKeys: ['reports.designer'],
  nav: [
    {
      key: 'reports',
      slot: 'main',
      label: 'Reports',
      to: '/app/reports',
      icon: DocumentTextIcon,
      permission: 'reports:read',
      featureKey: 'reports.designer',
      order: 40,
    },
  ],
  routes: [
    {
      path: '/app/reports',
      element: lazy(() => import('./pages/ReportsListPage').then((m) => ({ default: m.ReportsListPage }))),
      chrome: 'shell',
      permission: 'reports:read',
      // P16 §6.1 collection family (story 2-146a).
      layoutFamily: 'collection',
    },
    {
      // The destination P06 §4 names. Reports list/detail never route through
      // `/app/items/:id` any more; a link that still carries an INSPECTION id
      // is REDIRECTED here by the detail page itself (see ReportDetailPage).
      path: '/app/reports/:reportId',
      element: lazy(() => import('./pages/ReportDetailPage').then((m) => ({ default: m.ReportDetailPage }))),
      chrome: 'shell',
      permission: 'reports:read',
      // P16 §6.1 workflow family (story 2-146b): a single Report's own view
      // (version history, approval status) is a record moving through
      // state, not a second list-composition family — same reasoning as
      // `/app/sites/:id` in this same story.
      layoutFamily: 'workflow',
    },
  ],
  // P09 §2.1 parity — the capabilities that shipped WITH these endpoints in
  // this same story (agents/src/capabilities/reports.js). `report_download`
  // added in story 4-79, which audited this surface against the render-model
  // (4-76) and worker-offloaded PDF pipeline (4-77) and closed the last
  // human-only path: an agent could say a PDF was ready but never hand it over.
  agentTools: [
    'reports_list',
    'report_get',
    'report_regenerate',
    'report_download',
    // Story 6-1a.
    'share_link_create',
    'share_link_list',
    'share_link_revoke',
  ],
  queryKeys: reportsKeys,
};
